import type {
  AvailabilityMap,
  Contributor,
  DateAvailabilityMap,
  ExperienceLevelMap,
  Solution } from
'../types';
import { getAdobeAccessToken, getAuthenticatedUserId } from '../auth/adobeIms';

const DATABASE_URL = "https://defaultfa7b1b5a7b34438794aed2c178dece.e1.environment.api.powerplatform.com:443/powerautomate/automations/direct/workflows/cb50f431ce0e4b6fb7135db4372dd837/triggers/manual/paths/invoke?api-version=1&sp=%2Ftriggers%2Fmanual%2Frun&sv=1.0&sig=_klmBgykoqXOEj37f213Sv7VQqRqUntCcxVBnkIQ0-s";
const DATABASE_READ_URL = "https://defaultfa7b1b5a7b34438794aed2c178dece.e1.environment.api.powerplatform.com:443/powerautomate/automations/direct/workflows/101210f89ad449c4b8e0ea899bca7058/triggers/manual/paths/invoke?api-version=1&sp=%2Ftriggers%2Fmanual%2Frun&sv=1.0&sig=AYYZ-qpPrqAHckrdr7E8ZOU_Z0_39lw3IMJ7rSWpDt8";

export interface DashboardStatePayload {
  contributors: Contributor[];
  solutions: Solution[];
  availability: AvailabilityMap;
  dateAvailability: DateAvailabilityMap;
  experienceLevels: ExperienceLevelMap;
}

function parseStateField<T>(value: unknown, fallback: T): T {
  if (value == null) {
    return fallback;
  }

  if (typeof value === 'string') {
    try {
      return JSON.parse(value) as T;
    } catch (error) {
      console.error('Failed to parse database state field', error);
      return fallback;
    }
  }

  return value as T;
}

function normalizeDashboardState(
payload: Record<string, unknown>)
: DashboardStatePayload | null {
  const rawState =
  payload.state as Record<string, unknown> | undefined || payload;

  const contributors = parseStateField<Contributor[]>(
    rawState.contributors || rawState.Contributors,
    []
  );
  const solutions = parseStateField<Solution[]>(rawState.solutions || rawState.Solutions, []);

  if (!Array.isArray(contributors) || !Array.isArray(solutions)) {
    return null;
  }

  return {
    contributors,
    solutions,
    availability: parseStateField<AvailabilityMap>(rawState.availability ? JSON.parse(rawState.availability) : {} || rawState.Availability ? JSON.parse(rawState.Availability) : {}, {}),
    dateAvailability: parseStateField<DateAvailabilityMap>(
      rawState.dateAvailability || rawState.DateAvailability,
      {}
    ),
    experienceLevels: parseStateField<ExperienceLevelMap>(
      rawState.experienceLevels || rawState.ExperienceLevels,
      {}
    )
  };
}

export async function fetchInitialStateFromDatabase():
Promise<DashboardStatePayload | null> {
  try {
    if (!DATABASE_READ_URL) {
      throw new Error('Missing VITE_DATABASE_READ_URL');
    }

    const response = await fetch(DATABASE_READ_URL, {
      method: 'GET',
      headers: {
        Accept: 'application/json'
      }
    });

    if (!response.ok) {
      throw new Error(`Failed to fetch initial state (${response.status})`);
    }

    const payload = await response.json();
    if (!payload || typeof payload !== 'object') {
      return null;
    }

    return normalizeDashboardState(payload as Record<string, unknown>);
  } catch (error) {
    alert("Data could not be fetched !")
    console.error('Error fetching initial state from Database:', error);
    return null;
  }
}


export async function submitToDatabase(
data: Record<string, any>)
: Promise<{success: boolean;error?: string;}> {
  try {
    if (!DATABASE_URL) {
      throw new Error('Missing URL');
    }

    const authenticatedUserId = getAuthenticatedUserId();
    const payload = { ...data };
    const accessToken = getAdobeAccessToken();

    if (payload.contributorId != null) {
      payload.targetContributorId = payload.contributorId;
    }

    payload.contributorId = authenticatedUserId || 'anonymous';
    payload.accessToken = accessToken || '';

    const response = await fetch(DATABASE_URL, {
      method: 'POST',
      // mode: 'no-cors', // Required for Google Apps Script
      headers: {
        'Content-Type': 'application/json'
      },
      body: JSON.stringify(payload)
    });

    return { success: true };
  } catch (error) {
    console.error('Error submitting to Database:', error);
    return {
      success: false,
      error: error instanceof Error ? error.message : 'Unknown error'
    };
  }
}

// Helper function to log availability changes
export async function logAvailabilityChange(
contributor: {id: string;name: string;region: string;},
solution: {id: string;name: string;},
status: string,
experienceLevel?: string)
{
  return submitToDatabase({
    actionType: 'Status Update',
    contributorId: contributor.id,
    contributorName: contributor.name,
    region: contributor.region,
    solutionId: solution.id,
    solutionName: solution.name,
    status,
    experienceLevel: experienceLevel || '',
    notes: `Status changed to ${status}${experienceLevel ? ` with level ${experienceLevel}` : ''}`,
    timestamp: new Date().toISOString()
  });
}

// Helper function to log experience level changes
export async function logExperienceLevelChange(
contributor: {id: string;name: string;region: string;},
solution: {id: string;name: string;},
experienceLevel: string | undefined,
status: string)
{
  return submitToDatabase({
    actionType: 'Experience Level Change',
    contributorId: contributor.id,
    contributorName: contributor.name,
    region: contributor.region,
    solutionId: solution.id,
    solutionName: solution.name,
    status,
    experienceLevel: experienceLevel || '',
    notes: experienceLevel ?
    `Experience level set to ${experienceLevel}` :
    'Experience level cleared',
    timestamp: new Date().toISOString()
  });
}

// Helper function to log OOO schedules
export async function logOOOSchedule(
contributor: {id: string;name: string;region: string;},
startDate: string,
endDate: string,
isClearing: boolean = false)
{
  return submitToDatabase({
    actionType: isClearing ? 'OOO Cleared' : 'OOO Schedule',
    contributorId: contributor.id,
    contributorName: contributor.name,
    region: contributor.region,
    startDate: isClearing ? '' : startDate,
    endDate: isClearing ? '' : endDate,
    notes: isClearing ?
    'Out of office cleared' :
    `Out of office from ${startDate} to ${endDate}`,
    timestamp: new Date().toISOString()
  });
}

// Helper function to log date-based availability overrides
export async function logDateOverride(
contributor: {id: string;name: string;region: string;},
solution: {id: string;name: string;},
startDate: string,
endDate: string,
status: string,
isDeleting: boolean = false)
{
  return submitToDatabase({
    actionType: isDeleting ? 'Date Override Deleted' : 'Date Override Added',
    contributorId: contributor.id,
    contributorName: contributor.name,
    region: contributor.region,
    solutionId: solution.id,
    solutionName: solution.name,
    status,
    startDate,
    endDate,
    notes: isDeleting ?
    `Removed scheduled ${status} from ${startDate} to ${endDate}` :
    `Scheduled ${status} from ${startDate} to ${endDate}`,
    timestamp: new Date().toISOString()
  });
}

// Helper function to log contributor region updates
export async function logRegionUpdate(
contributor: {id: string;name: string;region: string;},
newRegion: string)
{
  return submitToDatabase({
    actionType: 'Region Update',
    contributorId: contributor.id,
    contributorName: contributor.name,
    region: newRegion,
    notes: `Region changed from ${contributor.region} to ${newRegion}`,
    timestamp: new Date().toISOString()
  });
}

// Helper function to log contributor edits
export async function logContributorEdit(
contributorId: string,
oldName: string,
newName: string,
oldRegion: string,
newRegion: string)
{
  return submitToDatabase({
    actionType: 'Contributor Edited',
    contributorId,
    contributorName: newName,
    region: newRegion,
    notes: `Name: ${oldName} → ${newName}, Region: ${oldRegion} → ${newRegion}`,
    timestamp: new Date().toISOString()
  });
}

// Helper function to log solution edits
export async function logSolutionEdit(
solutionId: string,
oldName: string,
newName: string)
{
  return submitToDatabase({
    actionType: 'Solution Edited',
    solutionId,
    solutionName: newName,
    notes: `Solution renamed from "${oldName}" to "${newName}"`,
    timestamp: new Date().toISOString()
  });
}

// Helper function to save complete state to Sheet2
export async function logStateToDatabase(
contributors: any[],
solutions: any[],
availability: any,
dateAvailability: any,
experienceLevels: any)
{
  return submitToDatabase({
    actionType: 'State Backup',
    sheetName: 'Sheet2',
    storageKey: 'FULL_STATE',
    contributors: JSON.stringify(contributors),
    solutions: JSON.stringify(solutions),
    availability: JSON.stringify(availability),
    dateAvailability: JSON.stringify(dateAvailability),
    experienceLevels: JSON.stringify(experienceLevels),
    timestamp: new Date().toISOString()
  });
}
